import { useEffect, useState } from "react";
import { useActivityApi } from "../../hooks/useActivityApi";
import { MemberDTO, ComplianceHistoryResponseDTO, ComplianceYearDTO } from "../../services/apiClient";
import { getActivityCountByType, getComplianceActivityTypes, isDedicatedCategory } from "../../utils/complianceUtils";
import ComplianceHistoryTable from "../tables/ComplianceHistoryTable";
import { MemberTypeConstants } from "../../constants/MemberTypeConstants";

interface ComplianceHistoryFullProps {
    member: MemberDTO | null;
}

interface ComplianceRow {
    complianceYear: string;
    certificateType: 'Hunt' | 'Sport';
    completed: 'Completed' | 'Not completed';
    status: string;
    compliantTo: string;
    membershipFees: 'Up to date' | 'Life member' | 'Overdue';
    datePaidUpTo: string;
    activity1: 'Completed' | 'Uncompleted' | 'Not needed';
    activity2: 'Completed' | 'Uncompleted' | 'Not needed';
    daysLeft: string;
    howToFix: string;
    originalYear: ComplianceYearDTO;
}

const ComplianceHistoryFull: React.FC<ComplianceHistoryFullProps> = ({ member }) => {
    const { getAllComplianceActivitiesHistory, isLoading, error } = useActivityApi();
    const [complianceHistory, setComplianceHistory] = useState<ComplianceHistoryResponseDTO | null>(null);
    const [requiredActivities, setRequiredActivities] = useState<any[]>([]);
    const [processedRows, setProcessedRows] = useState<ComplianceRow[]>([]);

    useEffect(() => {

        const fetchAllComplianceHistory = async () => {
            if (!member?.memberCategoryId) return;

            try {
                const response = await getAllComplianceActivitiesHistory();
                setComplianceHistory(response?.data || null);
            } catch (error) {
                console.error('Error fetching All Compliance Activities History', error);
            }
        };

        fetchAllComplianceHistory();
    }, [member]);

    useEffect(() => {
        if (member) {
            setRequiredActivities(getComplianceActivityTypes(member.memberCategoryId!, member));
        }
    }, [member]);

    // Process the data to create the table structure
    useEffect(() => {
        if (!complianceHistory?.complianceYears || !member) return;

        const rows: ComplianceRow[] = [];
        const currentDate = new Date();

        // Find the last compliant year for "Compliant To" calculation
        const findLastCompliantYear = (certificateType: 'Hunt' | 'Sport') => {
            for (const year of complianceHistory.complianceYears!) {
                const relevantActivityType = requiredActivities.find(act =>
                    certificateType === 'Hunt' ? act.type.includes('Hunt') : act.type.includes('Sport')
                );

                if (relevantActivityType) {
                    const count = getActivityCountByType(year.activities || [], relevantActivityType.activityTypeId);
                    const isCompliant = count >= relevantActivityType.requiredCount;
                    const certificateStatus = year.certificateStatus;
                    const statusValue = certificateType === 'Hunt'
                        ? certificateStatus?.hunter
                        : certificateStatus?.sport;

                    // If certificate did not exist yet for this year, skip
                    if (!statusValue) {
                        continue;
                    }

                    const isNeeded = statusValue === 'needed';

                    if (isCompliant && isNeeded) {
                        return year.title || '';
                    }
                }
            }
            return '';
        };

        // Calculate membership status
        const getMembershipStatus = () => {
            // Check if member is a life member
            const isLifeMember = member?.memberTypeId?.toLowerCase() === MemberTypeConstants.LIFE_MEMBER.toLowerCase() ||
                member?.memberTypeId?.toLowerCase() === MemberTypeConstants.DEDICATED_LIFE_MEMBER.toLowerCase();

            if (isLifeMember) return 'Life member';
            if (!member.paidUpTo) return 'Overdue';
            const expiryDate = new Date(member.paidUpTo);
            if (expiryDate > currentDate) return 'Up to date';
            return 'Overdue';
        };

        // Calculate days left until end of compliance year (birthday-based)
        const calculateDaysLeft = (complianceYear: ComplianceYearDTO) => {
            if (!member.dateOfBirth) return 0;

            const birthDate = new Date(member.dateOfBirth);
            const birthMonth = birthDate.getMonth(); // 0-11 (January = 0)

            // Compliance year title might be formatted like "JUL 2025" or just "2025"
            const title = complianceYear.title || '';

            // Try to extract year from the title
            const yearMatch = title.match(/(\d{4})/);
            if (!yearMatch) return 0;

            const yearNumber = parseInt(yearMatch[1]);

            // The compliance year ends on the LAST DAY of the birthday month
            // For July 15 birthday: compliance year ends July 31st
            // Get last day of month by going to next month day 0
            const endDate = new Date(yearNumber, birthMonth + 1, 0, 23, 59, 59);

            // Calculate days remaining
            const diffTime = endDate.getTime() - currentDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? diffDays : 0;
        };

        complianceHistory.complianceYears.forEach((year) => {
            const yearNumber = year.title || '';

            // Check if we need Hunt and/or Sport rows for this year
            const needsHunt = requiredActivities.some(act => act.type.includes('Hunt'));
            const needsSport = requiredActivities.some(act => act.type.includes('Sport'));

            // Render Hunt only if the certificate status exists for this year
            if (needsHunt && year.certificateStatus?.hunter) {
                const huntActivityType = requiredActivities.find(act => act.type.includes('Hunt'));
                const huntCount = huntActivityType ? getActivityCountByType(year.activities || [], huntActivityType.activityTypeId) : 0;
                const huntRequired = huntActivityType?.requiredCount || 2;
                const isHuntCompliant = huntCount >= huntRequired;
                const isHuntNeeded = year.certificateStatus?.hunter === 'needed';

                // Calculate status based on whether all activities are completed
                const activity1Status = !isHuntNeeded ? 'Not needed' : (huntCount >= 1 ? 'Completed' : 'Uncompleted');
                const activity2Status = !isHuntNeeded ? 'Not needed' : (huntCount >= 2 ? 'Completed' : 'Uncompleted');
                const membershipStatus = getMembershipStatus();

                // Status is "Completed" only if all green columns are completed or not needed
                const allActivitiesGreen = (activity1Status === 'Completed' || activity1Status === 'Not needed') &&
                    (activity2Status === 'Completed' || activity2Status === 'Not needed') &&
                    (membershipStatus === 'Up to date' || membershipStatus === 'Life member');

                const calculatedStatus = allActivitiesGreen ? 'Completed' : 'Not completed';
                const daysLeftStatus = allActivitiesGreen ? 'Completed' : String(calculateDaysLeft(year));

                rows.push({
                    complianceYear: `${yearNumber} (Hunt)`,
                    certificateType: 'Hunt',
                    completed: isHuntCompliant && isHuntNeeded ? 'Completed' : 'Not completed',
                    status: calculatedStatus,
                    compliantTo: findLastCompliantYear('Hunt'),
                    membershipFees: membershipStatus,
                    datePaidUpTo: member.paidUpTo ? new Date(member.paidUpTo).toLocaleDateString() : 'N/A',
                    activity1: activity1Status,
                    activity2: activity2Status,
                    daysLeft: daysLeftStatus,
                    howToFix: isHuntCompliant || !isHuntNeeded ? 'Not required' : `Log ${huntRequired - huntCount} activities • Maintain status information`,
                    originalYear: year
                });
            }

            // Render Sport only if the certificate status exists for this year
            if (needsSport && year.certificateStatus?.sport) {
                const sportActivityType = requiredActivities.find(act => act.type.includes('Sport'));
                const sportCount = sportActivityType ? getActivityCountByType(year.activities || [], sportActivityType.activityTypeId) : 0;
                const sportRequired = sportActivityType?.requiredCount || 2;
                const isSportCompliant = sportCount >= sportRequired;
                const isSportNeeded = year.certificateStatus?.sport === 'needed';

                // Calculate status based on whether all activities are completed
                const activity1Status = !isSportNeeded ? 'Not needed' : (sportCount >= 1 ? 'Completed' : 'Uncompleted');
                const activity2Status = !isSportNeeded ? 'Not needed' : (sportCount >= 2 ? 'Completed' : 'Uncompleted');
                const membershipStatus = getMembershipStatus();

                // Status is "Completed" only if all green columns are completed or not needed
                const allActivitiesGreen = (activity1Status === 'Completed' || activity1Status === 'Not needed') &&
                    (activity2Status === 'Completed' || activity2Status === 'Not needed') &&
                    (membershipStatus === 'Up to date' || membershipStatus === 'Life member');

                const calculatedStatus = allActivitiesGreen ? 'Completed' : 'Not completed';
                const daysLeftStatus = allActivitiesGreen ? 'Completed' : String(calculateDaysLeft(year));

                rows.push({
                    complianceYear: `${yearNumber} (Sport)`,
                    certificateType: 'Sport',
                    completed: isSportCompliant && isSportNeeded ? 'Completed' : 'Not completed',
                    status: calculatedStatus,
                    compliantTo: findLastCompliantYear('Sport'),
                    membershipFees: membershipStatus,
                    datePaidUpTo: member.paidUpTo ? new Date(member.paidUpTo).toLocaleDateString() : 'N/A',
                    activity1: activity1Status,
                    activity2: activity2Status,
                    daysLeft: daysLeftStatus,
                    howToFix: isSportCompliant || !isSportNeeded ? 'Not required' : `Log ${sportRequired - sportCount} activities • Maintain status information`,
                    originalYear: year
                });
            }
        });

        // Sort by year (newest first)
        rows.sort((a, b) => {
            const yearA = parseInt(a.complianceYear.split(' ')[0]);
            const yearB = parseInt(b.complianceYear.split(' ')[0]);
            return yearB - yearA;
        });

        setProcessedRows(rows);
    }, [complianceHistory, member, requiredActivities]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="text-gray-500">Loading compliance history...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <div className="text-red-600 text-center">
                    Error loading compliance history: {error}
                </div>
            </div>
        );
    }

    if (!member || !isDedicatedCategory(member.memberCategoryId!)) {
        return null;
    }

    return (
        <div className="p-4">
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Compliance History</h3>
                <p className="text-sm text-gray-600">Complete compliance history with separate Hunt and Sport tracking</p>
            </div>

            <ComplianceHistoryTable
                data={processedRows}
                isLoading={isLoading}
            />
        </div>
    );
};

export default ComplianceHistoryFull;